(function () {
  if (window.JRBUsWinnerMap) {
    return;
  }

  class JRBUsWinnerMapInstance {
    constructor(root) {
      this.root = root;
      this.section = root.closest('[data-us-winner-map-section]');
      this.stage = root.querySelector('[data-us-winner-map-stage]');
      this.panel = root.querySelector('[data-us-winner-map-panel]');
      this.status = root.querySelector('[data-us-winner-map-status]');
      this.svg = root.querySelector('.us-winner-map__svg');
      this.pins = Array.from(root.querySelectorAll('[data-us-winner-map-pin]'));
      this.persistentPanelMode = root.dataset.persistentPanelMode || 'disabled';
      this.activePin = null;
      this.previewState = null;
      this.mobileQuery = window.matchMedia('(max-width: 767px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.handleRootClick = this.handleRootClick.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    init() {
      if (!this.root || this.root.dataset.usWinnerMapReady === 'true') {
        return;
      }

      this.root.dataset.usWinnerMapReady = 'true';
      this.section.classList.add('is-enhanced');
      this.root.addEventListener('click', this.handleRootClick);
      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleDocumentKeydown);
      window.addEventListener('resize', this.handleResize);

      this.pins.forEach((pin) => {
        pin.addEventListener('mouseenter', () => this.applyPreviewState(pin));
        pin.addEventListener('mouseleave', () => this.clearPreviewState());
        pin.addEventListener('focus', () => this.applyPreviewState(pin));
        pin.addEventListener('blur', () => this.clearPreviewState());
      });

      this.ensurePersistentDefaultPin();
    }

    destroy() {
      this.root.removeEventListener('click', this.handleRootClick);
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('keydown', this.handleDocumentKeydown);
      window.removeEventListener('resize', this.handleResize);
      this.root.dataset.usWinnerMapReady = 'false';
    }

    handleRootClick(event) {
      const closeTrigger = event.target.closest('[data-us-winner-map-close]');
      if (closeTrigger) {
        event.preventDefault();
        if (this.isPersistentPanelActive()) {
          return;
        }
        this.closePanel();
        return;
      }

      const pin = event.target.closest('[data-us-winner-map-pin]');
      if (!pin || !this.root.contains(pin)) {
        return;
      }

      event.preventDefault();

      if (this.activePin === pin) {
        if (this.isPersistentPanelActive()) {
          return;
        }
        this.closePanel();
        return;
      }

      this.openPin(pin);
    }

    handleDocumentClick(event) {
      if (!this.activePin || this.isPersistentPanelActive()) {
        return;
      }

      if (this.section.contains(event.target)) {
        return;
      }

      this.closePanel();
    }

    handleDocumentKeydown(event) {
      if (event.key === 'Escape' && this.activePin && !this.isPersistentPanelActive()) {
        event.preventDefault();
        this.closePanel();
        this.activePin.focus();
      }
    }

    handleResize() {
      this.ensurePersistentDefaultPin();

      if (this.activePin) {
        this.positionPanel(this.activePin);
      }
    }

    getDefaultPin() {
      return this.pins.length ? this.pins[this.pins.length - 1] : null;
    }

    isPersistentPanelActive() {
      if (this.persistentPanelMode === 'both') {
        return true;
      }

      if (this.persistentPanelMode === 'desktop') {
        return !this.mobileQuery.matches;
      }

      if (this.persistentPanelMode === 'mobile') {
        return this.mobileQuery.matches;
      }

      return false;
    }

    ensurePersistentDefaultPin() {
      if (!this.isPersistentPanelActive() || this.activePin || !this.pins.length) {
        return;
      }

      const defaultPin = this.getDefaultPin();
      if (defaultPin) {
        this.openPin(defaultPin, { skipScroll: true });
      }
    }

    openPin(pin, options = {}) {
      const templateId = pin.dataset.templateId;
      const template = templateId ? document.getElementById(templateId) : null;
      if (!template) {
        return;
      }

      this.pins.forEach((item) => {
        item.classList.toggle('is-active', item === pin);
        item.setAttribute('aria-expanded', item === pin ? 'true' : 'false');
      });

      this.activePin = pin;
      this.panel.innerHTML = template.innerHTML;
      this.panel.hidden = false;
      this.panel.classList.add('is-open');
      this.applyActiveState(pin);
      this.positionPanel(pin);
      this.status.textContent = `Showing winner details for ${pin.getAttribute('aria-label').replace('Open winner details for ', '')}.`;

      const panelImage = this.panel.querySelector('img');
      if (panelImage) {
        panelImage.addEventListener('load', this.handleResize, { once: true });
      }

      if (this.mobileQuery.matches && !this.isPersistentPanelActive() && !options.skipScroll) {
        window.requestAnimationFrame(() => {
          this.panel.scrollIntoView({
            behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
            block: 'nearest'
          });
        });
      }
    }

    closePanel() {
      this.pins.forEach((item) => {
        item.classList.remove('is-active');
        item.setAttribute('aria-expanded', 'false');
      });

      this.activePin = null;
      this.panel.hidden = true;
      this.panel.classList.remove('is-open');
      this.panel.innerHTML = '';
      this.clearMapStates();
      this.status.textContent = 'Winner details closed.';
    }

    positionPanel(pin) {
      if (!pin || this.mobileQuery.matches || this.isPersistentPanelActive()) {
        this.panel.style.removeProperty('--uswm-panel-left');
        this.panel.style.removeProperty('--uswm-panel-top');
        return;
      }

      const stageRect = this.stage.getBoundingClientRect();
      const pinRect = pin.getBoundingClientRect();
      const panelRect = this.panel.getBoundingClientRect();
      const gutter = 16;
      const offset = 18;
      const pinCenterX = (pinRect.left - stageRect.left) + (pinRect.width / 2);
      const pinCenterY = (pinRect.top - stageRect.top) + (pinRect.height / 2);

      let left = pinCenterX + offset;
      if (left + panelRect.width > stageRect.width - gutter) {
        left = pinCenterX - panelRect.width - offset;
      }
      if (left < gutter) {
        left = Math.min(
          Math.max(pinCenterX - (panelRect.width / 2), gutter),
          stageRect.width - panelRect.width - gutter
        );
      }

      let top = pinCenterY - (panelRect.height / 2);
      if (top < gutter) {
        top = pinCenterY + offset;
      }
      if (top + panelRect.height > stageRect.height - gutter) {
        top = Math.max(pinCenterY - panelRect.height - offset, gutter);
      }

      this.panel.style.setProperty('--uswm-panel-left', `${left}px`);
      this.panel.style.setProperty('--uswm-panel-top', `${top}px`);
    }

    clearMapStates() {
      if (!this.svg) {
        return;
      }

      this.svg.querySelectorAll('.is-active, .is-preview').forEach((node) => {
        node.classList.remove('is-active', 'is-preview');
      });
      this.previewState = null;
    }

    applyPreviewState(pin) {
      const stateCode = this.normalizeStateCode(pin.dataset.stateCode);
      if (!this.shouldHighlightState(pin) || !stateCode || (this.activePin && this.activePin.dataset.stateCode === stateCode)) {
        return;
      }

      this.clearPreviewState();
      const stateNode = this.findStateNode(stateCode);
      if (stateNode) {
        stateNode.classList.add('is-preview');
        this.previewState = stateNode;
      }
    }

    clearPreviewState() {
      if (this.previewState) {
        this.previewState.classList.remove('is-preview');
        this.previewState = null;
      }
    }

    applyActiveState(pin) {
      this.clearMapStates();
      const stateCode = this.normalizeStateCode(pin.dataset.stateCode);
      if (!this.shouldHighlightState(pin) || !stateCode) {
        return;
      }

      const stateNode = this.findStateNode(stateCode);
      if (stateNode) {
        stateNode.classList.add('is-active');
      }
    }

    shouldHighlightState(pin) {
      return !!pin && pin.dataset.highlightState === 'true';
    }

    findStateNode(stateCode) {
      if (!this.svg || !stateCode) {
        return null;
      }

      try {
        return this.svg.querySelector(`.${stateCode}`);
      } catch (error) {
        return null;
      }
    }

    normalizeStateCode(value) {
      if (!value) {
        return '';
      }

      return String(value).toLowerCase().replace(/[^a-z]/g, '').slice(0, 2);
    }
  }

  window.JRBUsWinnerMap = {
    instances: new Map(),
    init(root) {
      if (!root) {
        return;
      }

      const sectionElement = root.closest('[data-us-winner-map-section]');
      const key = sectionElement ? sectionElement.id : `us-winner-map-${Date.now()}`;
      if (this.instances.has(key)) {
        return;
      }

      const instance = new JRBUsWinnerMapInstance(root);
      instance.init();
      this.instances.set(key, instance);
    },
    unload(sectionElement) {
      if (!sectionElement) {
        return;
      }

      const instance = this.instances.get(sectionElement.id);
      if (instance) {
        instance.destroy();
        this.instances.delete(sectionElement.id);
      }
    }
  };

  const initAll = (scope) => {
    (scope || document).querySelectorAll('[data-us-winner-map-root]').forEach((root) => {
      window.JRBUsWinnerMap.init(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    window.JRBUsWinnerMap.unload(event.target);
  });
})();
